import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  SecurityContext,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Event } from '../../../../shared/model/event';
import { Organization } from '../../../../shared/model/organization';
import { GenericFormComponent } from '../../../../shared/components/generic-form/generic-form.component';
import { LocationFormComponent } from '../../../../shared/components/location-form/location-form.component';
import { GeoLocationService } from '../../services/geo-location.service';
import { DomSanitizer } from '@angular/platform-browser';
import { QuillModule } from 'ngx-quill';
import { pastDateValidator } from '../../../../shared/validators/date.validators';

@Component({
  selector: 'app-event-form',
  standalone: true,
  imports: [ReactiveFormsModule, GenericFormComponent, LocationFormComponent, QuillModule],
  templateUrl: './event-form.component.html',
  styleUrl: './event-form.component.css',
})
export class EventFormComponent implements OnChanges {
  private fb: FormBuilder = inject(FormBuilder);
  private geoLocationService: GeoLocationService = inject(GeoLocationService);
  private sanitizer: DomSanitizer = inject(DomSanitizer);

  @Input() title: string = 'Evento';
  @Input() submitLabel: string = 'Salva';
  @Input() initialData?: Event;
  @Input() availableOrganizations: Organization[] = [];
  @Input() lockOrganization = false;
  @Output() formSubmit = new EventEmitter<Partial<Event>>();
  @Output() cancel = new EventEmitter<void>();

  protected eventForm: FormGroup;
  protected readonly formId = 'eventForm';
  protected readonly quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ],
  };

  private endDateManuallyChanged = false;

  constructor() {
    this.eventForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      imageUrl: [''],
      organizationId: [null as number | null, Validators.required],
      location: this.fb.group({
        id: [''],
        formalName: [''],
        address: [''],
        city: [''],
        country: [''],
        town: [''],
        village: [''],
        neighbourhood: [''],
        residential: [''],
        postcode: [''],
        street: [''],
        houseNumber: [''],
        province: [''],
        latitude: [undefined],
        longitude: [undefined],
        rawInput: [''],
        displayName: [''],
        lat: [''],
        lon: [''],
      }),
      startDate: ['', [Validators.required, pastDateValidator()]],
      endDate: ['', [pastDateValidator()]],
    }, { validators: this.dateRangeValidator });

    this.eventForm.get('startDate')?.valueChanges.subscribe((newStartDate) => {
      const endDateControl = this.eventForm.get('endDate');
      if (endDateControl && !this.endDateManuallyChanged) {
        endDateControl.setValue(newStartDate ?? '', { emitEvent: false });
      }
    });

    this.eventForm.get('endDate')?.valueChanges.subscribe(() => {
      const endDateControl = this.eventForm.get('endDate');
      if (endDateControl?.dirty) {
        this.endDateManuallyChanged = true;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialData'] && this.initialData) {
      const locationValue = this.geoLocationService.normalizeLocation(this.initialData.location);
      const startDate = this.normalizeDate(this.initialData.startDate);
      const endDate = this.normalizeDate(this.initialData.endDate);
      const resolvedEndDate = (endDate && endDate.trim()) ? endDate : startDate;
      this.endDateManuallyChanged = Boolean(endDate && endDate.trim() && endDate !== startDate);

      this.eventForm.patchValue({
        name: this.initialData.name,
        description: this.initialData.description,
        imageUrl: this.initialData.imageUrl,
        location: locationValue,
        startDate: startDate,
        endDate: resolvedEndDate,
        organizationId: this.initialData.organization?.id ?? null,
      });
      if (this.lockOrganization) {
        this.eventForm.get('organizationId')?.disable({ emitEvent: false });
      }
    }
  }

  onSubmit(): void {
    if (this.eventForm.valid) {
      const value = this.eventForm.getRawValue();
      const selectedOrganization = this.availableOrganizations.find(
        (org) => org.id === Number(value.organizationId),
      );

      // La data di fine se non specificata o non modificata prende la data di inizio
      const effectiveEndDate = (value.endDate && value.endDate.trim())
        ? value.endDate.trim()
        : value.startDate;

      this.formSubmit.emit({
        name: value.name,
        description: this.sanitizedHtml(value.description),
        imageUrl: value.imageUrl,
        location: this.geoLocationService.toApiLocation(value.location) ?? undefined,
        startDate: value.startDate,
        endDate: effectiveEndDate,
        active: this.initialData ? this.initialData.active : true,
        organization: selectedOrganization
          ? { id: selectedOrganization.id, organizationName: selectedOrganization.organizationName }
          : this.initialData?.organization,
      });
    }
  }

  private normalizeDate(value?: string): string {
    if (!value || !value.trim()) {
      return '';
    }
    let date = value.trim();
    if (date.includes('T')) {
      date = date.split('T')[0];
    } else if (date.includes(' ')) {
      date = date.split(/\s+/)[0];
    }
    const itMatch = date.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (itMatch) {
      date = `${itMatch[3]}-${itMatch[2]}-${itMatch[1]}`;
    }
    return date;
  }

  private dateRangeValidator(form: FormGroup): { endBeforeStart: boolean } | null {
    const startDate = form.get('startDate')?.value;
    const endDate = form.get('endDate')?.value;

    if (!startDate || !endDate) {
      return null;
    }

    if (endDate < startDate) {
      return { endBeforeStart: true };
    }

    return null;
  }

  onCancel(): void {
    this.cancel.emit();
  }

  protected sanitizedHtml(value: unknown): string {
    return this.sanitizer.sanitize(SecurityContext.HTML, String(value ?? ''))?.trim() ?? '';
  }
}
